import React from 'react';
import { Check, TrendingUp, Users, Award } from 'lucide-react';

const PricingSection: React.FC = () => {
  const plans = [
    {
      name: 'TRIAL SESSION',
      price: 100,
      icon: TrendingUp,
      description: 'Perfect for first-timers',
      features: [
        'Full fitness assessment',
        'Personalized workout plan',
        'Form correction & guidance',
        'Goal setting consultation',
        'No commitment required'
      ],
      highlighted: false
    },
    {
      name: 'SINGLE SESSION',
      price: 150,
      icon: Award,
      description: 'Flexible pay-as-you-go',
      features: [
        'One-on-one training',
        'Customized workout',
        'Progress tracking',
        'Nutrition guidance',
        'Schedule flexibility'
      ],
      highlighted: false
    },
    {
      name: '12-PACK',
      price: 145,
      total: 1740,
      icon: Users,
      description: 'Best value for commitment',
      features: [
        'Save $60 total',
        'Priority scheduling',
        'Weekly progress reviews',
        'Nutrition plan included',
        'Access to app tracking',
        'Free posture assessment'
      ],
      highlighted: true,
      badge: 'MOST POPULAR'
    }
  ];

  return (
    <section id="pricing" className="py-20 bg-dark">
      <div className="container mx-auto px-6">
        <h2 className="font-bebas text-5xl md:text-7xl text-center text-white mb-4 tracking-wider">
          PLANS & <span className="text-primary">PRICING</span>
        </h2>
        
        <p className="text-gray-400 font-montserrat text-center mb-16 max-w-2xl mx-auto">
          Invest in your health with transparent pricing and no hidden fees. 
          Choose the plan that fits your goals and lifestyle.
        </p>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative transform transition-all duration-500 hover:scale-105 ${
                plan.highlighted ? 'md:-mt-4' : ''
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary text-white px-4 py-1 text-xs font-montserrat font-bold tracking-wider rounded-full z-10">
                  {plan.badge}
                </div>
              )}
              
              <div className={`h-full bg-gradient-to-b ${
                plan.highlighted 
                  ? 'from-primary/20 to-black/50 border-2 border-primary' 
                  : 'from-black/50 to-black/30 border-2 border-gray-800'
              } backdrop-blur-sm p-8`}>
                <plan.icon className={`${plan.highlighted ? 'text-primary' : 'text-gray-400'} mb-4`} size={32} />
                
                <h3 className="font-bebas text-2xl text-white mb-2 tracking-wider">
                  {plan.name}
                </h3>
                
                <p className="text-gray-400 font-montserrat text-sm mb-6">
                  {plan.description}
                </p>

                <div className="mb-6">
                  <span className="text-5xl font-bebas text-white">${plan.price}</span>
                  {plan.total ? (
                    <>
                      <span className="text-gray-400 font-montserrat text-sm"> /session</span>
                      <div className="text-primary font-montserrat font-semibold mt-2">
                        Total: ${plan.total}
                      </div>
                    </>
                  ) : (
                    <span className="text-gray-400 font-montserrat text-sm"> /session</span>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <Check className="text-primary mr-2 mt-0.5 flex-shrink-0" size={16} />
                      <span className="text-gray-300 font-montserrat text-sm">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button 
                  onClick={() => {
                    const element = document.querySelector('#booking');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={`w-full py-3 font-montserrat font-bold tracking-wider transition-all duration-300 ${
                    plan.highlighted
                      ? 'bg-primary hover:bg-red-600 text-white'
                      : 'bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-white'
                  }`}
                >
                  GET STARTED
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Money Back Guarantee */}
        <div className="text-center mt-12 p-6 bg-black/30 backdrop-blur-sm max-w-2xl mx-auto">
          <p className="text-primary font-bebas text-2xl mb-2">100% SATISFACTION GUARANTEE</p>
          <p className="text-gray-400 font-montserrat text-sm">
            If you're not completely satisfied with your first session, we'll refund your money. No questions asked.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;